import type { Component } from 'vue';
import { entityModel } from '@/composables/entityColumns';
import { readBucketId, readNestedDisplayString } from '@/utils/nestedRelation';
import { readZoneDisplayString, resolveZoneLocation, splitZoneDisplayString } from '@/utils/zoneDisplayString';
import { playlistLabelFor, readResolvedPlaylist } from '@/composables/api/useResolvedPlaylistNames';
import SearchResultInspector from '@/components/DataTable/SearchResultInspector.vue';
import LocationZonePlayerInspector from '@/components/DataTable/LocationZonePlayerInspector.vue';
import LocationZonePlayerOverlayHeader from '@/components/DataTable/LocationZonePlayerOverlayHeader.vue';
import ModelRecordOverlayHeader from '@/components/DataTable/ModelRecordOverlayHeader.vue';
import PlaylistContainerInspector from '@/components/DataTable/PlaylistContainerInspector.vue';
import LiveTrackInspector from '@/components/DataTable/LiveTrackInspector.vue';
import LiveTrackSearchCardHeader from '@/components/DataTable/LiveTrackSearchCardHeader.vue';

/**
 * Per-entity inspector mapping shared by every surface that shows a record's
 * detail — the cards split pane, the side-peek overlay opened from a master
 * row, and the id-column "open in side peek" record inspector — so they all
 * render the SAME view for a given entity.
 */
export function inspectorComponentFor(entityId: string): Component {
    if (entityId === 'LocationZonePlayer') {
        return LocationZonePlayerInspector;
    }
    // The two playlist track containers get a Details + Tracks tabbed inspector.
    if (isPlaylistContainerEntity(entityId)) {
        return PlaylistContainerInspector;
    }
    // Same pull-down, Details + Lyrics — the only way into the lyrics view that
    // doesn't require adding the lyrics_check_status column to a table first.
    if (entityId === 'LiveTrack') {
        return LiveTrackInspector;
    }
    return SearchResultInspector;
}

/**
 * Slim header rendered in the overlay inspector's title bar in place of the
 * plain title. LocationZonePlayer has a bespoke live status line; everything
 * else gets the generic display-string header ("<display_string>", falling
 * back to "<Display Name> #<id>").
 */
export function overlayHeaderComponentFor(entityId: string): Component | null {
    return entityId === 'LocationZonePlayer'
        ? LocationZonePlayerOverlayHeader
        : ModelRecordOverlayHeader;
}

/**
 * The entity's fixed card for the "Fixed Card" search layout: a rich,
 * entity-specific card rendered INSTEAD of the dynamic column summary (the
 * layout also drops the Column Settings control). Null means the entity has
 * no fixed card and the option is not offered.
 */
export function fixedCardHeaderComponentFor(entityId: string): Component | null {
    if (entityId === 'LiveTrack') {
        return LiveTrackSearchCardHeader;
    }
    return null;
}

/**
 * The two track containers — a bucket, and the custom list that draws from one.
 *
 * One table, because the pair was being spelled out in several places and the
 * copies drifted: the card said "No source bucket" for a record the inspector
 * showed a link for. Everything per-entity about a container lives here, and
 * every consumer reads it through the helpers below.
 *
 * `parentBucketField` is the nested `{id, display_string, bucket_type}`
 * reference naming the bucket this container's tracks come from. `vaultFlag` is
 * a flat boolean the row may carry beside it, which settles the parent's mark
 * with no request; only the custom list has one (`base_bucket_is_vault` does
 * NOT exist in the index and must never be invented).
 *
 * `followedVaultField` is the nested reference naming the vault that the
 * parent bucket follows, so a card can say "<bucket> follows <vault>". Only the
 * custom list has one. A bucket's own vault is already its parent bucket.
 */
const PLAYLIST_CONTAINERS = Object.freeze({
    Bucket: {
        parentBucketField: 'base_bucket',
        // No empty state: a bucket with no base bucket shows just its name.
        missingParentLabel: null,
        vaultFlag: null,
        followedVaultField: null,
    },
    LocationZoneCustomPlaylist: {
        parentBucketField: 'source_bucket',
        // No empty state: most lists have no source bucket, so the slot (and
        // its separator) is simply left out.
        missingParentLabel: null,
        vaultFlag: 'source_bucket_is_vault',
        followedVaultField: 'source_bucket_base_bucket',
    },
} as const);

type PlaylistContainerEntity = keyof typeof PLAYLIST_CONTAINERS;

export function isPlaylistContainerEntity(entityId: string): entityId is PlaylistContainerEntity {
    return entityId in PLAYLIST_CONTAINERS;
}

/** The nested reference's field name, or null for an entity with no parent bucket. */
export function parentBucketFieldFor(entityId: string): 'base_bucket' | 'source_bucket' | null {
    return isPlaylistContainerEntity(entityId) ? PLAYLIST_CONTAINERS[entityId].parentBucketField : null;
}

/** What the slot says when the record genuinely has no parent bucket. */
export function missingParentBucketLabelFor(entityId: string): string | null {
    return isPlaylistContainerEntity(entityId) ? PLAYLIST_CONTAINERS[entityId].missingParentLabel : null;
}

/** A container's parent bucket, as far as one row knows it. */
export interface ParentBucketRef {
    /** The bucket's name, or null when the row names none. */
    name: string | null;
    /** Its id, or null. A link needs BOTH — an id with no name is a button with no text in it. */
    id: number | string | null;
    /** What `modelIconForRow('Bucket', …)` reads, or null when there is no reference at all. */
    iconRow: Record<string, unknown> | null;
    /** What to print when `name` is null; null means print nothing at all. */
    missingLabel: string | null;
}

/**
 * Read a container's parent bucket off one row. Null for an entity that has no
 * parent bucket at all, so a caller can tell "not a container" from "a
 * container with nothing in the slot".
 *
 * Both shapes are accepted, because both turn up: the nested reference a search
 * document carries, and the flat `<field>_id` / `<field>_name` pair a REST
 * payload has instead (the Playlist Switching report really does select
 * `sb.name as source_bucket_name`). Deriving the flat names from the field is
 * what stops a Bucket reading a custom list's `source_bucket_id` and reporting
 * that it follows no vault.
 */
export function readParentBucket(
    entityId: string,
    row: Record<string, unknown> | null | undefined
): ParentBucketRef | null {
    if (!isPlaylistContainerEntity(entityId)) {
        return null;
    }
    const { parentBucketField, missingParentLabel, vaultFlag } = PLAYLIST_CONTAINERS[entityId];
    if (!row) {
        return { name: null, id: null, iconRow: null, missingLabel: missingParentLabel };
    }

    const nested = row[parentBucketField];
    const nestedRow =
        nested && typeof nested === 'object' && !Array.isArray(nested)
            ? (nested as Record<string, unknown>)
            : null;

    const flatName = row[`${parentBucketField}_name`];
    const name =
        readNestedDisplayString(nested) ??
        (typeof flatName === 'string' && flatName.trim() !== '' ? flatName.trim() : null);

    // readBucketId, not readNestedId: a Bucket with no vault carries
    // base_bucket_id = 0, and taking that for an id made every such card
    // request bucket 0 and report a stale index to Sentry (TABASCO-1WN).
    // A custom list's source_bucket_id has a foreign key, so it never holds 0,
    // and the same reader is simply correct for it too.
    const flatId = row[`${parentBucketField}_id`];
    const id = readBucketId(nestedRow?.id) ?? readBucketId(flatId);

    // The vault flag settles the mark without a request. Fed in as
    // `is_base_bucket`, which is the field the bucket-mark resolver reads.
    const iconRow =
        nestedRow === null
            ? null
            : vaultFlag !== null && row[vaultFlag] === true
              ? { ...nestedRow, is_base_bucket: true }
              : nestedRow;

    return { name, id, iconRow, missingLabel: missingParentLabel };
}

/**
 * Read the vault that a container's parent bucket follows off one row. Only a
 * custom list carries one, as `source_bucket_base_bucket`.
 *
 * Null when the row has no such key (a document indexed before the field
 * existed), when the key is null (the source bucket follows no vault, or is a
 * vault itself), and when the vault has no name. A nameless vault would draw
 * "follows" with nothing after it, so it is left out.
 *
 * The icon row is marked as a vault, because it is one. Its mark is then
 * drawn from the row, and resolving it never makes a request.
 */
export function readFollowedVault(
    entityId: string,
    row: Record<string, unknown> | null | undefined
): ParentBucketRef | null {
    if (!isPlaylistContainerEntity(entityId) || !row) {
        return null;
    }
    const { followedVaultField } = PLAYLIST_CONTAINERS[entityId];
    if (followedVaultField === null) {
        return null;
    }

    const nested = row[followedVaultField];
    if (!nested || typeof nested !== 'object' || Array.isArray(nested)) {
        return null;
    }
    const name = readNestedDisplayString(nested);
    if (name === null) {
        return null;
    }
    const nestedRow = nested as Record<string, unknown>;

    return {
        name,
        id: readBucketId(nestedRow.id),
        iconRow: { ...nestedRow, is_base_bucket: true },
        missingLabel: null,
    };
}

/**
 * Column ids the CARD layouts render regardless of Column Settings — the
 * entity's fixed header/thumb fields. The Column Settings list keeps these
 * checked and disabled while a card layout is active (and skips them in
 * Select All) so the picker can't promise to hide something the card shows
 * anyway.
 *
 * Entities without a bespoke card header still have a fixed header: the
 * generic card row always renders the title (display_string ?? name) plus the
 * id. So the default pins id + the fields that COMPOSE that heading — `name`
 * for most entities, the display_string's component fields where the heading
 * is assembled (Contact: first_name + last_name), or id alone for entities
 * with neither (LiveTrackAction,
 * LocationZonePlayerParsedPlaylistTrack, whose header shows display_string /
 * #id only).
 */
export function cardFixedColumnIdsFor(entityId: string): string[] {
    if (entityId === 'LocationZonePlayer') {
        // The status header: id, location, zone, online light, time-since-check-in.
        return ['id', 'location', 'location_zone', 'player_data_is_online', 'player_data_updated_at'];
    }
    if (entityId === 'LiveTrack') {
        // Default card header (title + id) and the floated cover thumb.
        return ['id', 'name', 'cover_art_signed_url'];
    }
    if (entityId === 'RawDdexTrack') {
        return ['id', 'title', 'cover_art_signed_url'];
    }

    if (isPlaylistContainerEntity(entityId)) {
        // The container header: the zone split against its location, the
        // name, then the source line with the parent bucket and the vault it
        // follows. All of it is drawn whether or not the columns are on, so
        // all of it is pinned. Otherwise the summary line would print the zone
        // a second time under a header that already shows it.
        const { parentBucketField, followedVaultField } = PLAYLIST_CONTAINERS[entityId];
        const fixedColumnIds: string[] = ['id', 'name', 'location_zone', 'location', parentBucketField];
        return followedVaultField === null ? fixedColumnIds : [...fixedColumnIds, followedVaultField];
    }

    if (entityId === 'LocationZoneCustomPlaylistSchedule') {
        // The schedule card: the list's name, the schedule's zone, and the same
        // schedule lines the custom list card draws. Everything it draws is
        // pinned, so the summary under it does not print the dates, times,
        // type and ordering a second time.
        return [
            'id',
            'location_zone',
            'location_zone_custom_playlist',
            'custom_playlist_schedule_type_id',
            'date',
            'end_date',
            'time',
            'end_time',
            'delimited_day_ids',
            'is_recurring',
            'recurring_start_date',
            'recurring_end_date',
            'recurring_weekly_interval',
            'insert_frequency_tracks_start',
            'insert_frequency_tracks_target',
            'insert_frequency_ramp_days',
            'messaging_start_time',
            'messaging_end_time',
            'messaging_frequency_minutes',
            'silence',
            'should_shuffle',
            'resume_from_file_offset',
            'loop',
            'priority',
        ];
    }

    if (entityId === 'Contact') {
        // The heading is the display_string, assembled from first_name +
        // last_name — pin the fields that compose it. Contact's `title` is the
        // JOB title (a separate fact the heading never shows); pinning it hid
        // it from the card entirely while locking its Column Settings checkbox.
        return ['id', 'first_name', 'last_name'];
    }

    const model = entityModel(entityId);
    if (model === undefined) {
        return [];
    }
    // Pin only what the heading genuinely renders: `name` when the model has
    // one (display_string is either the name itself or built around it), else
    // just the id. Never pin `title` — no generic heading renders it.
    if ('name' in model) {
        return ['id', 'name'];
    }
    return ['id'];
}

/**
 * What a card on the list page draws besides its header, as the page decides it.
 * The page already holds each of these flags, so it passes them in.
 */
export interface CardDrawnParts {
    /** The Fixed Card layout is on, so a Live Track card draws its rich header. */
    fixedCard: boolean;
    /** The card draws its tag chips. */
    tags: boolean;
    /** The card draws the Live Track rating chip. */
    rating: boolean;
    /** The card draws a zone player's Currently Playing block. */
    currentlyPlaying: boolean;
    /** The player's current track code resolved to a track, so the card draws its name instead of the code. */
    currentTrackResolved: boolean;
}

function readText(value: unknown): string | null {
    return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function readFiniteNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function hasTag(tags: unknown): boolean {
    const list = Array.isArray(tags) ? tags : tags ? [tags] : [];
    return list.some((tag) => readNestedDisplayString(tag) !== null);
}

/**
 * The quick-search match keys (SAU-591) that a card's header draws for one row.
 * A match on one of these keys is highlighted where the header draws it, so the
 * card adds no extra line for it. A match on any other key gets the extra line.
 *
 * Each condition below copies the template condition that draws the field. They
 * must stay in step. A key counted here that the header did not draw loses its
 * match, with no highlight and no extra line. The headers say the same beside
 * each condition. The title and the summary columns are counted by
 * SearchResultListRow itself, so they are not listed here.
 */
export function cardShownMatchFields(entityId: string, row: Record<string, unknown>, drawn: CardDrawnParts): string[] {
    const shown: string[] = [];

    if (isPlaylistContainerEntity(entityId)) {
        // PlaylistContainerZoneLine draws nothing when the zone has no name.
        const zoneDisplay = readZoneDisplayString(row.location_zone);
        if (zoneDisplay !== null) {
            shown.push('location_zone.display_string');
            // The zone line draws one location, as the left half of a zone name
            // that splits or after a name that does not. On a master zone it is
            // the master's location, so it counts only when it is the row's own.
            const drawnLocation = readNestedDisplayString(resolveZoneLocation(row.location_zone, row.location));
            const ownLocation = readNestedDisplayString(row.location);
            if (drawnLocation !== null && drawnLocation === ownLocation) {
                shown.push('location.display_string');
            }
        }
        // The parent bucket is never counted. Line one stays one line, so a
        // match in a clipped bucket name keeps the extra line.
    }

    if (entityId === 'LocationZonePlayer') {
        shown.push('id');
        const zoneDisplay = readZoneDisplayString(row.location_zone);
        if (zoneDisplay !== null) {
            shown.push('location_zone.display_string');
        }
        // LocationZonePlayerCardHeader draws the row's location in the heading
        // when the zone name opens with it, and on row two when it does not.
        if (readZoneDisplayString(row.location) !== null) {
            shown.push('location.display_string');
        }
        const zoneLocation =
            row.location_zone && typeof row.location_zone === 'object'
                ? readZoneDisplayString((row.location_zone as { location?: unknown }).location)
                : null;
        if (splitZoneDisplayString(zoneDisplay, zoneLocation).location !== null) {
            shown.push('location_zone.location.display_string');
        }
        if (readNestedDisplayString(row.account) !== null) {
            shown.push('account.display_string');
        }
    }

    if (entityId === 'LiveTrack' && drawn.fixedCard) {
        // LiveTrackSearchCardHeader: the title is the name with its remix, and
        // the copyright under the cover is hidden when it is 0.
        const hasName = readText(row.name) !== null;
        if (hasName) {
            shown.push('name');
            if (readText(row.remix_name) !== null) {
                shown.push('remix_name');
            }
        }
        if (readText(row.code) !== null) {
            shown.push('code');
        }
        for (const key of ['artist', 'album', 'genre']) {
            if (readNestedDisplayString(row[key]) !== null) {
                shown.push(`${key}.display_string`);
            }
        }
        if (readFiniteNumber(row.bitrate) !== null) {
            shown.push('bitrate');
        }
        if (readText(row.isrc) !== null) {
            shown.push('isrc');
        }
        const copyright = readFiniteNumber(row.copyright);
        if (copyright !== null && copyright !== 0) {
            shown.push('copyright');
        }
    }

    // The page's slots: CardTags and LzpCurrentlyPlayingCard.
    if (drawn.tags && hasTag(row.tags)) {
        shown.push('tags.display_string');
    }
    if (drawn.rating) {
        shown.push('rating');
    }
    if (drawn.currentlyPlaying) {
        if (readText(row.player_data_current_track_code) !== null && !drawn.currentTrackResolved) {
            shown.push('player_data_current_track_code');
        }
        // PlaylistWithBucketLink draws the name a reported filename resolved
        // to. It counts only while the label it draws is the reported name.
        const reportedName = readText(row.player_data_current_playlist_name)?.trim() ?? null;
        if (reportedName !== null) {
            const resolution = readResolvedPlaylist(reportedName, typeof row.id === 'number' ? row.id : null);
            if (playlistLabelFor(resolution, reportedName) === reportedName) {
                shown.push('player_data_current_playlist_name');
            }
        }
    }

    return shown;
}

/** Lives in cardDefaultColumns.ts so the saved view definition can use it. */
export { cardDefaultColumnIdsFor } from '@/composables/cardDefaultColumns';

/**
 * How often (ms) to re-fetch a record while its inspector is open, or undefined
 * to fetch once. LocationZonePlayers carry live check-in state (now-playing,
 * volume, online status, time-since-check-in) that changes underneath the open
 * inspector, so they poll on the same cadence as the commands/logs cards; other
 * entities are effectively static records and don't.
 */
export const LIVE_RECORD_POLL_MS = 30_000;

/**
 * Faster cadence for the ONE record whose inspector/detail pane is open.
 * The visible rows keep the 30s LIVE_RECORD_POLL_MS; the record the user is
 * actually looking at refreshes every 10s, and its fresh values flow back
 * into the row list through the query-cache bridge in
 * useSearchResultsLivePolling so the two views can never drift apart.
 */
export const INSPECTED_RECORD_POLL_MS = 10_000;

export function liveRecordPollMsFor(entityId: string): number | undefined {
    return entityId === 'LocationZonePlayer' ? INSPECTED_RECORD_POLL_MS : undefined;
}

/**
 * Fields the entity's fixed header already shows (always visible regardless
 * of Column Settings), excluded from the inspector's field sections.
 */
export function inspectorExcludeFieldsFor(entityId: string): string[] {
    if (entityId === 'LocationZonePlayer') {
        return ['id', 'location', 'location_zone', 'player_data_is_online', 'player_data_updated_at'];
    }
    if (isPlaylistContainerEntity(entityId)) {
        // The heading's zone line names the location half and the zone half as
        // two links, so both leave Details. The parent bucket stays: the spec
        // moves only the zone out of the field list.
        return ['id', 'location', 'location_zone'];
    }
    return ['id'];
}
